/**
 * Persistent ±N strike-window selector.
 * Window size is stored in localStorage so it survives reloads.
 *
 * Values:
 *   15  →  show ±15 strikes around spot
 *   30  →  show ±30 strikes around spot (default)
 *   0   →  show ALL strikes
 */

import { useCallback, useEffect, useState } from 'react';
import { STORAGE_KEYS } from '../lib/constants';

const STORAGE_KEY = STORAGE_KEYS.strikeWindow;
export type StrikeWindow = 15 | 30 | 0;
const VALID: StrikeWindow[] = [15, 30, 0];

function readStored(): StrikeWindow {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const n = raw == null ? NaN : Number(raw);
    if (VALID.includes(n as StrikeWindow)) return n as StrikeWindow;
  } catch { /* localStorage unavailable */ }
  return 30;
}

export function useStrikeWindow() {
  const [window, setWindow] = useState<StrikeWindow>(() => readStored());

  const update = useCallback((next: StrikeWindow) => {
    if (!VALID.includes(next)) return;
    try {
      localStorage.setItem(STORAGE_KEY, String(next));
    } catch { /* ignore */ }
    setWindow(next);
  }, []);

  // Listen for cross-tab changes.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      setWindow(readStored());
    };
    globalThis.addEventListener('storage', onStorage);
    return () => globalThis.removeEventListener('storage', onStorage);
  }, []);

  return [window, update] as const;
}
